import { gsap } from 'gsap';
import {
  Container,
  InteractionEvent,
  IPointData,
  NineSlicePlane,
  Texture,
} from '../../odie';

import { pixiSetEnabledFilter } from '../pixi/pixiTools';
import { app } from '../../game/app';
import { Lock } from '../../lib/pattern/Lock';
import type { BasicHandler } from '../defs/types';

// types
//-----------------------------------------------------------------------------
export type AnimateState = 'up' | 'down';

export type BasicButtonOptions = {
  width?: number;
  height?: number;
  up: string | Texture;
  down?: string | Texture;
  selected?: string | Texture;
  disabled?: string | Texture;
  slices?: {
    left?: number;
    top?: number;
    right?: number;
    bottom?: number;
  };
  sound?: string;
  noanimate?: boolean;
  contentAnchor?: IPointData;
};

// animations
//-----------------------------------------------------------------------------
const animationMap = {
  up: (container: Container) =>
    gsap.to(container, {
      duration: 0.2,
      scaleX: 1,
      scaleY: 1,
      ease: 'back.out',
    }),

  down: (container: Container) =>
    gsap.to(container, {
      duration: 0.2,
      scaleX: 0.84,
      scaleY: 0.84,
      ease: 'power1.out',
    }),
};

/*
    basic button with 9slice background and animation
*/
export class BasicButton extends Container {
  public onTap?: BasicHandler;
  public onDown?: BasicHandler;
  public onUp?: BasicHandler;
  public onOut?: BasicHandler;
  public onSelected?: (selected: boolean) => void;
  // state
  private _down = false;
  private _enabled = true;
  private _selected?: boolean = undefined;
  // components
  private _tapLock = new Lock();

  // properties
  //-------------------------------------------------------------------------
  public get down(): boolean {
    return this._down;
  }

  public get enabled(): boolean {
    return this._enabled;
  }

  public set enabled(value: boolean) {
    // if changing
    if (this._enabled !== value) {
      // update state
      this._enabled = value;
      this.interactive = value;
      this.buttonMode = value;

      // notify
      this.onBaseEnabled(value);
    }
  }

  public get selected(): boolean {
    return !!this._selected;
  }

  public set selected(value: boolean) {
    // if changing
    if (this._selected !== value) {
      // update state
      this._selected = value;

      // notify
      this.onBaseSelected(value);
      this.onSelected?.(value);
    }
  }

  public initListeners() {
    // register events
    // console.log('setting deferred listeners');
    this.interactive = true;
    this.setOnTap(() => {
      this._onPointerTap();
    });
  }

  // private: event handlers
  //-------------------------------------------------------------------------
  private async _onPointerTap(event?: InteractionEvent) {
    // lock to allow handler to complete
    if (this._tapLock.lock()) {
      // set down state
      this._down = false;

      // handle
      await this.onTap?.();
      this.onBaseTap(event);

      // unlock
      this._tapLock.unlock();
    }
  }
  // fields
  //-------------------------------------------------------------------------
  // input
  private _sound?: string | null;
  private _noanimate?: boolean;
  // scene
  private _content!: Container;
  private _background!: NineSlicePlane;
  // state
  private _activeTween?: gsap.core.Tween;
  private _textures!: {
    up: Texture;
    down?: Texture;
    disabled?: Texture;
    selected?: Texture;
  };

  // properties
  //-------------------------------------------------------------------------
  public get content(): Container {
    return this._content;
  }

  public get textures() {
    return this._textures;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: BasicButtonOptions) {
    super();
    if (options.width) this.width = options.width;
    if (options.height) this.height = options.height;

    // set fields
    this._sound =
      options.sound === null ? null : options.sound || 'button-basic';
    this._noanimate = options.noanimate;

    // create textures
    this._createTextures(options);

    // spawn content container
    this._spawnContent(options);

    // spawn background
    this._spawnBackground(options);

    this._update();
  }

  // impl
  //-------------------------------------------------------------------------
  public updateUpTexture(texture: string): void {
    this._createTextures({ up: texture } as BasicButtonOptions);
    this._update();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onBaseTap(event?: InteractionEvent): void {
    // play sound
    if (this._sound) app.sound.play(this._sound, { dupes: 2 });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onBaseDown(event?: InteractionEvent): void {
    this._update();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onBaseUp(event?: InteractionEvent): void {
    this._update();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onBaseSelected(value: boolean): void {
    this._update();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onBaseEnabled(value: boolean): void {
    this._update();
  }

  // private: updaters
  //-------------------------------------------------------------------------
  private _update() {
    // update texture
    this._background.texture = this._getActiveTexture();
    this._background.width = this.width;
    this._background.height = this.height;

    // if (this.selected) {
    //   this.setBackgroundColor('#009900');
    // } else {
    //   this.setBackgroundColor('#004400');
    // }

    // update disabled
    this._updateDisabled();

    // animate
    if (!this._noanimate) {
      // reset current animation
      this._activeTween?.kill();

      // start new aniamtion
      this._activeTween = animationMap[this._getAnimateState()](this._content);
    }
  }

  private _updateDisabled() {
    // set enabled filter
    pixiSetEnabledFilter(this, this.enabled);
  }

  // private: scene
  //-------------------------------------------------------------------------
  private _spawnContent(options: BasicButtonOptions) {
    // create content container
    const content = (this._content = new Container());
    if (options.width) content.width = options.width;
    if (options.height) content.height = options.height;

    // spawn
    this.addChild(content);
  }

  private _createTexture(
    source: string | Texture | undefined,
  ): Texture | undefined {
    return typeof source === 'string' ? Texture.from(source) : source;
  }

  private _createTextures(options: BasicButtonOptions) {
    // create textures
    this._textures = {
      up: this._createTexture(options.up)!,
      down: this._createTexture(options.down),
      selected: this._createTexture(options.selected),
      disabled: this._createTexture(options.disabled),
    };

    // if missing use width and height from up texture
    const up = this._textures.up;

    if (!options.width) {
      options.width = up.width;
    }
    if (!options.height) {
      options.height = up.height;
    }
  }

  private _spawnBackground(options: BasicButtonOptions) {
    const slices = options.slices || { left: 0, top: 0, right: 0, bottom: 0 };

    // create 9slice background
    this._background = new NineSlicePlane(
      this._textures.up,
      slices.left ?? 0,
      slices.top ?? 0,
      slices.right ?? 0,
      slices.bottom ?? 0,
    );

    // size it
    this._background.width = options.width!;
    this._background.height = options.height!;

    // spawn
    this._content.addChild(this._background);
  }

  // private: support
  //-------------------------------------------------------------------------
  private _getActiveTexture(): Texture {
    const textures = this._textures; // down

    if (this.down && textures.down) return textures.down;
    // enabled
    if (this.enabled) {
      // selected
      if (this.selected && textures.selected) return textures.selected;
      // disabled
    } else if (textures.disabled) return textures.disabled;

    // up/default
    return textures.up;
  }

  private _getAnimateState(): AnimateState {
    return this.down ? 'down' : 'up';
  }
}
